import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Users, 
  Utensils, 
  Apple,
  Dumbbell,
  Heart,
  Leaf,
  Activity,
  Building,
  ChevronRight,
  CheckCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const partnerTypes = [
  { id: "trainer", label: "Personal Trainer", icon: <Users className="w-5 h-5" /> },
  { id: "meal_prep", label: "Meal Prep Company", icon: <Utensils className="w-5 h-5" /> },
  { id: "nutrition", label: "Nutrition Coach", icon: <Apple className="w-5 h-5" /> },
  { id: "gym", label: "Gym / Fitness Studio", icon: <Dumbbell className="w-5 h-5" /> },
  { id: "recovery", label: "Recovery Services", icon: <Heart className="w-5 h-5" /> },
  { id: "wellness", label: "Wellness Professional", icon: <Leaf className="w-5 h-5" /> },
  { id: "fitness_class", label: "Fitness Classes", icon: <Activity className="w-5 h-5" /> },
  { id: "other", label: "Other Health Service", icon: <Building className="w-5 h-5" /> },
];

const PartnerApplyPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    partner_type: "",
    location: "",
    description: "",
    website: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeSelect = (typeId) => {
    setFormData(prev => ({ ...prev, partner_type: typeId }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.partner_type) {
      toast.error("Please select a partner type");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/partners/apply`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setIsSubmitted(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <main className="min-h-screen bg-black flex items-center justify-center px-6" data-testid="partner-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 rounded-full bg-[#00FFD1]/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-[#00FFD1]" />
          </div>
          <h1 className="text-3xl font-black text-white mb-4">Application Submitted!</h1>
          <p className="text-gray-400 mb-8">
            Thank you for your interest in partnering with EVOLVE. We'll review your application and get back to you within 2-3 business days.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#00FFD1] text-black font-bold rounded-full hover:bg-[#00FFD1]/90 transition-all"
          >
            Back to Home
            <ChevronRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-black py-12 px-6" data-testid="partner-apply-page">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-[#00FFD1] transition-colors mb-8"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="text-3xl md:text-4xl font-black text-white tracking-tight mb-4">
            Partner With <span className="text-[#00FFD1]">EVOLVE</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Join our network of fitness and wellness professionals. Connect with motivated clients in your area.
          </p>
        </motion.div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Partner Type Selection */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <label className="block text-white font-semibold mb-4">
              What type of service do you offer? <span className="text-red-400">*</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {partnerTypes.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleTypeSelect(type.id)}
                  className={`p-4 rounded-xl border transition-all text-left ${
                    formData.partner_type === type.id
                      ? "bg-[#00FFD1]/10 border-[#00FFD1] text-[#00FFD1]"
                      : "bg-white/5 border-white/10 text-gray-400 hover:border-white/30"
                  }`}
                  data-testid={`partner-type-${type.id}`}
                >
                  <div className="mb-2">{type.icon}</div>
                  <span className="text-sm font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Personal Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid sm:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-white font-semibold mb-2">
                Your Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
                placeholder="John Smith"
                data-testid="partner-name-input"
              />
            </div>
            <div>
              <label className="block text-white font-semibold mb-2">
                Email <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
                placeholder="john@example.com"
                data-testid="partner-email-input"
              />
            </div>
          </motion.div>

          {/* Contact & Business */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid sm:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-white font-semibold mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
                placeholder="(555) 123-4567"
                data-testid="partner-phone-input"
              />
            </div>
            <div>
              <label className="block text-white font-semibold mb-2">
                Business Name
              </label>
              <input
                type="text"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
                placeholder="Fitness Pro LLC"
                data-testid="partner-business-input"
              />
            </div>
          </motion.div>

          {/* Location */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <label className="block text-white font-semibold mb-2">
              Location / Service Area <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
              placeholder="Los Angeles, CA"
              data-testid="partner-location-input"
            />
          </motion.div>

          {/* Website */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <label className="block text-white font-semibold mb-2">
              Website or Social Media
            </label>
            <input
              type="url"
              name="website"
              value={formData.website}
              onChange={handleChange}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all"
              placeholder="https://yourwebsite.com"
              data-testid="partner-website-input"
            />
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <label className="block text-white font-semibold mb-2">
              Tell us about your services <span className="text-red-400">*</span>
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-gray-500 focus:border-[#00FFD1] focus:ring-1 focus:ring-[#00FFD1] outline-none transition-all resize-none"
              placeholder="Describe your services, experience, certifications, and what makes you unique..."
              data-testid="partner-description-input"
            />
          </motion.div>

          {/* Submit */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <button
              type="submit"
              disabled={isLoading}
              className="w-full sm:w-auto px-8 py-4 bg-[#00FFD1] text-black font-bold rounded-full hover:bg-[#00FFD1]/90 transition-all shadow-[0_0_20px_rgba(0,255,209,0.3)] hover:shadow-[0_0_30px_rgba(0,255,209,0.5)] disabled:opacity-70 flex items-center justify-center gap-2"
              data-testid="partner-submit-btn"
            >
              {isLoading ? "Submitting..." : "Submit Application"}
              {!isLoading && <ChevronRight className="w-4 h-4" />}
            </button>
          </motion.div>
        </form>
      </div>
    </main>
  );
};

export default PartnerApplyPage;
